import { get, ref, serverTimestamp, set } from "firebase/database";
import i18next from "i18next";
import { db } from "../lib/firebase";
import types from "../types";
import { LaunchError } from "../utils/errors";
import { listenDatabaseValue } from "../utils/listenDatabaseValue";
import {
  ADLE_CARD_WAITLIST_PATH,
  ADLE_CARD_WAITLIST_PROMPTS_PATH,
  ADLE_CARD_WAITLIST_SOURCE,
  buildAdleCardWaitlistSignup,
  isAdleCardWaitlistSignupWritable,
} from "../utils/adleCardWaitlist";
import { isRtdbPermissionDenied } from "../utils/rtdbErrors.js";

const WRITE_TIMEOUT_MS = 15000;

const sentryMeta = (functionName) => ({
  functionName,
  tags: { feature: "adleCardWaitlist" },
  capture: "exception",
});

const withTimeout = ({ promise, timeoutMs, message }) =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      const error = new Error(message);
      error.code = "timeout";
      reject(error);
    }, timeoutMs);

    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(error);
      }
    );
  });

export const setAdleCardWaitlistStatus = (payload) => ({
  type: types.UI_SET_ADLE_CARD_WAITLIST_STATUS,
  payload,
});

export const startListenerAdleCardWaitlistStatus =
  ({ uid, onUpdatedDataCallback }) =>
  (dispatch) => {
    if (!uid) {
      onUpdatedDataCallback?.({
        hasJoined: false,
        hasDismissed: false,
        isStatusResolved: true,
      });
      return () => {};
    }

    let signupLoaded = false;
    let promptLoaded = false;
    let hasJoined = false;
    let hasDismissed = false;

    const emit = () => {
      onUpdatedDataCallback?.({
        hasJoined,
        hasDismissed,
        isStatusResolved: signupLoaded && promptLoaded,
      });
    };

    // listenDatabaseValue reports non-permission failures itself and swallows
    // permission-denied (logout). This listener opts that case back in, and
    // always marks the node resolved so the prompt does not wait forever.
    const reportListenerPermissionDenied = (error) => {
      if (!isRtdbPermissionDenied(error)) return;
      dispatch(
        LaunchError(error, false, [], {
          ...sentryMeta("startListenerAdleCardWaitlistStatus"),
          reportPermissionDenied: true,
        })
      );
    };

    const unsubscribeSignup = listenDatabaseValue(
      ref(db, `${ADLE_CARD_WAITLIST_PATH}/${uid}`),
      (snapshot) => {
        signupLoaded = true;
        hasJoined = snapshot.exists();
        emit();
      },
      (error) => {
        signupLoaded = true;
        hasJoined = false;
        emit();
        reportListenerPermissionDenied(error);
      }
    );

    const unsubscribePrompt = listenDatabaseValue(
      ref(db, `${ADLE_CARD_WAITLIST_PROMPTS_PATH}/${uid}`),
      (snapshot) => {
        promptLoaded = true;
        hasDismissed = snapshot.val()?.dismissedAt != null;
        emit();
      },
      (error) => {
        promptLoaded = true;
        hasDismissed = false;
        emit();
        reportListenerPermissionDenied(error);
      }
    );

    return () => {
      if (typeof unsubscribeSignup === "function") unsubscribeSignup();
      if (typeof unsubscribePrompt === "function") unsubscribePrompt();
    };
  };

export const startSaveAdleCardWaitlistSignup =
  ({ email, country, source }) =>
  async (dispatch, getState) => {
    const user = getState()?.auth?.user;
    const uid = user?.uid;
    const agencyID = user?.agencyID;
    const advertiserID = user?.advertiserID;

    try {
      // RTDB requires agencyID === auth.token.agencyID. Do not fall back to
      // advertiserID when the claim is missing.
      if (typeof agencyID !== "string" || !agencyID) {
        const error = new Error(
          "Missing agencyID auth claim for Adle Card waitlist"
        );
        dispatch(
          LaunchError(
            error,
            false,
            [],
            sentryMeta("startSaveAdleCardWaitlistSignup")
          )
        );
        return { ok: false };
      }

      if (typeof advertiserID !== "string" || !advertiserID) {
        const error = new Error(
          "Missing advertiserID auth claim for Adle Card waitlist"
        );
        dispatch(
          LaunchError(
            error,
            false,
            [],
            sentryMeta("startSaveAdleCardWaitlistSignup")
          )
        );
        return { ok: false };
      }

      const safeSource =
        source === ADLE_CARD_WAITLIST_SOURCE.BANNER
          ? ADLE_CARD_WAITLIST_SOURCE.BANNER
          : ADLE_CARD_WAITLIST_SOURCE.AUTO;

      const signup = buildAdleCardWaitlistSignup({
        uid,
        email,
        accountEmail: typeof user?.email === "string" ? user.email : undefined,
        country,
        agencyID,
        advertiserID,
        language: i18next.language || "",
        source: safeSource,
      });

      // The form already validated email and country, so a payload the rules
      // would reject here is a bug, not user input.
      if (!isAdleCardWaitlistSignupWritable(signup)) {
        dispatch(
          LaunchError(
            new Error("Adle Card waitlist signup failed local validation"),
            false,
            [],
            sentryMeta("startSaveAdleCardWaitlistSignup")
          )
        );
        return { ok: false };
      }

      const creationTime = Date.now();
      await withTimeout({
        promise: set(ref(db, `${ADLE_CARD_WAITLIST_PATH}/${uid}`), {
          ...signup,
          creationTime,
          creationTimeNegative: -creationTime,
        }),
        timeoutMs: WRITE_TIMEOUT_MS,
        message: "Adle Card waitlist write timed out",
      });

      return { ok: true };
    } catch (error) {
      if (isRtdbPermissionDenied(error) && uid) {
        try {
          const snapshot = await withTimeout({
            promise: get(ref(db, `${ADLE_CARD_WAITLIST_PATH}/${uid}`)),
            timeoutMs: WRITE_TIMEOUT_MS,
            message: "Adle Card waitlist read timed out",
          });
          if (snapshot.exists()) {
            return { ok: true, alreadyJoined: true };
          }
        } catch (readError) {
          if (!isRtdbPermissionDenied(readError)) {
            dispatch(
              LaunchError(
                readError,
                false,
                [],
                sentryMeta("startSaveAdleCardWaitlistSignup")
              )
            );
          }
          return { ok: false };
        }
        return { ok: false };
      }

      dispatch(
        LaunchError(
          error,
          false,
          [],
          sentryMeta("startSaveAdleCardWaitlistSignup")
        )
      );
      return { ok: false };
    }
  };

export const startDismissAdleCardWaitlist =
  () => async (dispatch, getState) => {
    try {
      const uid = getState()?.auth?.user?.uid;
      if (!uid) return { ok: false };

      await set(ref(db, `${ADLE_CARD_WAITLIST_PROMPTS_PATH}/${uid}`), {
        dismissedAt: serverTimestamp(),
      });
      dispatch(setAdleCardWaitlistStatus({ hasDismissed: true }));
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(
          error,
          false,
          [],
          sentryMeta("startDismissAdleCardWaitlist")
        )
      );
      return { ok: false };
    }
  };

export const startGetAdleCardWaitlistSignups = () => async (dispatch) => {
  try {
    const snapshot = await get(ref(db, ADLE_CARD_WAITLIST_PATH));
    return { ok: true, data: snapshot.val() || {} };
  } catch (error) {
    dispatch(
      LaunchError(
        error,
        false,
        [],
        sentryMeta("startGetAdleCardWaitlistSignups")
      )
    );
    return { ok: false };
  }
};

export const startGetAgencyNamesByIDs =
  (agencyIDs = []) =>
  async (dispatch) => {
    try {
      const uniqueIDs = [
        ...new Set(
          (Array.isArray(agencyIDs) ? agencyIDs : []).filter(
            (id) => typeof id === "string" && id
          )
        ),
      ];
      if (uniqueIDs.length === 0) {
        return { ok: true, data: {} };
      }

      const entries = await Promise.all(
        uniqueIDs.map(async (agencyID) => {
          const snapshot = await get(ref(db, `agencies/${agencyID}/name`));
          const name = snapshot.val();
          return [agencyID, typeof name === "string" ? name.trim() : ""];
        })
      );

      return { ok: true, data: Object.fromEntries(entries) };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], sentryMeta("startGetAgencyNamesByIDs"))
      );
      return { ok: false, data: {} };
    }
  };

export const startGetAdvertiserBusinessNamesByIDs =
  (advertiserIDs = []) =>
  async (dispatch) => {
    try {
      const uniqueIDs = [
        ...new Set(
          (Array.isArray(advertiserIDs) ? advertiserIDs : []).filter(
            (id) => typeof id === "string" && id
          )
        ),
      ];
      if (uniqueIDs.length === 0) {
        return { ok: true, data: {} };
      }

      const entries = await Promise.all(
        uniqueIDs.map(async (advertiserID) => {
          const snapshot = await get(
            ref(db, `advertisers/${advertiserID}/businessName`)
          );
          const name = snapshot.val();
          return [advertiserID, typeof name === "string" ? name.trim() : ""];
        })
      );

      return { ok: true, data: Object.fromEntries(entries) };
    } catch (error) {
      dispatch(
        LaunchError(
          error,
          false,
          [],
          sentryMeta("startGetAdvertiserBusinessNamesByIDs")
        )
      );
      return { ok: false, data: {} };
    }
  };
